import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import {
  Card,
  FailureMessage,
  ProgressCircle,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import {
  useLoadMatterFile,
  useToggleSlug,
  useUpdateAIUserFeedback,
} from "@smart/manage-gql-client-dom";

import { GeneratingFeedback } from "./generating-feedback";
import { FormLoadingStatus } from "../../create-form/creation-status";
import { GqlForm } from "../../types";

export type GeneratingFormProp = {
  title: string;
  loadingStatus: FormLoadingStatus;
  creationStatus: string | null | undefined;
  aiUserFeedback?: GqlForm["aiUserFeedback"] | null;
  updateAIUserFeedback: ReturnType<typeof useUpdateAIUserFeedback>[0];
  updating?: boolean;
  form?: GqlForm | null;
};

const messageText: Record<string, string | undefined> = {
  fileUploaded: "Uploading your file...",
  processingFile: "Processing file...",
  "failedToGenerateQuestions-fileEncrypted": "Processing file...",
  generatingQuestions: "Generating form...",
  savingQuestions: "Saving form...",
  duplicating: "Duplicating form...",
  reverting: "Reverting form...",
};

export const GeneratingForm = ({
  title,
  loadingStatus,
  creationStatus,
  aiUserFeedback,
  updateAIUserFeedback,
  updating,
  form,
}: GeneratingFormProp) => {
  const { aiUserFeedback: feedbackFeatureFlag } = useFeatureFlags();
  const [updateSlug] = useToggleSlug();
  const { matters, files } = useSmokeballApp();
  const fileId = aiUserFeedback?.sourceFileIds?.[0] || "";
  const matterId = matters?.current?.id || "";
  const sourceFile = useLoadMatterFile(
    { matterId, fileId },
    { skip: !matterId || !fileId },
  );

  if (creationStatus === "translating" && loadingStatus === "checking") {
    return (
      <div className="fixed inset-0 flex justify-center items-center z-modal">
        <ProgressCircle size="large" description="Loading language versions" />
      </div>
    );
  }

  if (
    (loadingStatus === "created" || !loadingStatus) &&
    (!aiUserFeedback ||
      aiUserFeedback?.status ===
        (feedbackFeatureFlag ? "reviewed" : "completed"))
  )
    return null;

  const activeText =
    aiUserFeedback?.status === "failed"
      ? "Form generation failed"
      : aiUserFeedback?.statusText ||
        (creationStatus && messageText[creationStatus]) ||
        messageText.generatingQuestions;

  return (
    <div
      className="sticky top-0 z-10 bg-gradient-to-b from-background via-background to-transparent mx-4 flex flex-col items-center"
      data-testid="generating-form-status"
    >
      <Card
        size="readable"
        flow="row"
        align="center"
        loading={
          loadingStatus === "checking" ||
          aiUserFeedback?.status === "generating"
        }
      >
        <div className="flex-1 p-4">
          <h2 className="font-semibold text-h4">{activeText}</h2>
          {sourceFile.result && (
            <div className="text-base">
              <button
                type="button"
                className="text-blue-600 hover:text-blue-800 underline bg-transparent border-none p-0 cursor-pointer focus:outline-none"
                onClick={() =>
                  sourceFile.result?.id &&
                  files?.open({ id: sourceFile.result.id })
                }
              >
                {sourceFile.result.name}
                {sourceFile.result.fileExtension}
              </button>
            </div>
          )}
          {loadingStatus !== "checking" && (
            <GeneratingFeedback
              title={title}
              aiUserFeedback={aiUserFeedback}
              updateAIUserFeedback={updateAIUserFeedback}
              updating={updating}
              updateSlug={updateSlug}
              form={form}
            />
          )}
          {loadingStatus === "fileEncrypted" && (
            <FailureMessage
              action="generate form"
              textOverride="Failed to generate questions with password protected document."
            />
          )}
          {loadingStatus === "timedOut" && (
            <FailureMessage
              action="generate form"
              textOverride="This is taking a long time. Please close the window and try again."
            />
          )}
        </div>
      </Card>
    </div>
  );
};
